import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import {
  formatUserRole,
  isLiveChatAgentOrFacebookPageCollaborator,
  UserRole,
} from '@mm/state/model/CompanyUsersModel';
import { companyAdminSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { LifeCycleThis } from '@mm/utils';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  compose,
  lifecycle,
  StateHandlerMap,
  withHandlers,
  withStateHandlers,
} from 'recompose';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { CheckBoxItem } from '../../../../../../../common/components/Checkbox';
import {
  CheckBoxesList,
  CheckboxListItem,
} from '../../../../../../../common/components/CheckBoxesList/CheckBoxList';
import { RouterComponentProps } from '../../../../../../../common/helpers/routes/interfaces';
import { SettingsRoutes } from '../../../../../../../routes/settings-routes';
import SettingSection from '../../../../components/SettingSection/SettingSection';
import './AccessUsers.pcss';
import { description, title } from './contants';

type OuterProps = {
  facebookPageId: number;
  companyId: number;
  [key: string]: any;
};

type StateProps = {
  items: CheckboxListItem[];
};

type StateHandlerProps = StateHandlerMap<StateProps> & {
  setItems: (items: CheckBoxItem[]) => Partial<StateProps>;
};

type PropsFromStateToProps = ReturnType<typeof mapStateToProps>;
type PropsFromDispatchToProps = ReturnType<typeof mapDispatchToProps>;

type PropsBeforeHandler = OuterProps &
  StateProps &
  StateHandlerProps &
  PropsFromStateToProps &
  PropsFromDispatchToProps;

type InnerProps = PropsFromStateToProps &
  PropsFromDispatchToProps &
  StateProps &
  StateHandlerProps &
  WithHandlersProps &
  RouterComponentProps;

type SetInitialValues = (props: PropsBeforeHandler) => () => void;
type OnChangeCheckbox = (props: PropsBeforeHandler) => (items: any[]) => void;

type WithHandlersProps = {
  onChangeCheckbox: OnChangeCheckbox;
};

type HandlerProps = {
  onChangeCheckbox: ReturnType<OnChangeCheckbox>;
};

type Props = InnerProps & OuterProps & HandlerProps;

const mapStateToProps = (state: StoreState) => ({
  users: state.companyUsers.entitiesFb,
  isCompanyAdmin: companyAdminSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getFacebookPageUsers: (facebookPageId: number, companyId: number) =>
    dispatch(
      usersCRUDActions.retriveAllByFacebookPage(
        facebookPageId,
        companyId,
        1,
        500,
      ),
    ),
  allowPageForUsers: (
    pageId: number,
    allowedUserIds: number[],
    allowedAgentsIds: number[],
  ) =>
    dispatch(
      connectedPageCRUDActions.allowPageForUsers(
        pageId,
        allowedUserIds,
        allowedAgentsIds,
      ),
    ),
});

const initialState = {
  items: [],
};

const stateUpdaters = {
  setItems: () => items => ({ items }),
};

const setInitialValues: SetInitialValues =
  ({ users, setItems }) =>
  () => {
    const updatedUsers = users.map(user => ({
      ...user,
      isChecked: user.permittedForFacebookPage,
      key: user.id.toString(),
      label: (
        <>
          {user.lastName} {user.firstName} -
          <span className="access-page__list-user-role">
            {formatUserRole(user.role)}
          </span>
        </>
      ),
      disabled: !isLiveChatAgentOrFacebookPageCollaborator(user.role),
    }));
    setItems(updatedUsers);
  };

const onChangeCheckbox: OnChangeCheckbox = props => items => {
  const { allowPageForUsers, facebookPageId, setItems } = props;

  const checked = items.filter(({ isChecked }) => isChecked);

  const [allowedAgentsIds, allowedUserIds] = checked.reduce(
    (a, { role, key }) => {
      role === UserRole.LiveChatAgent ? a[0].push(key) : a[1].push(key);
      return a;
    },
    [[], []],
  );

  setItems(items);
  allowPageForUsers(facebookPageId, allowedUserIds, allowedAgentsIds);
};

function componentDidMount(this: LifeCycleThis<Props>) {
  const { facebookPageId, getFacebookPageUsers, companyId } = this.props;
  getFacebookPageUsers(facebookPageId, companyId);
}

function componentDidUpdate(this: LifeCycleThis<Props>, prevProps) {
  const { users: prevUsers } = prevProps;
  const { users, setInitialValues } = this.props;

  if (users !== prevUsers) setInitialValues();
}

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ setInitialValues, onChangeCheckbox }),
  lifecycle<Props, {}>({ componentDidMount, componentDidUpdate }),
);

const AccessUsers = (props: Props) => {
  const { items, onChangeCheckbox, isCompanyAdmin, history } = props;
  const disabled =
    !isCompanyAdmin || items.every(item => item.disabled || false);
  return (
    <SettingSection title={title}>
      <p className="access-page__description">{description}</p>
      <div className="access-page__list">
        <CheckBoxesList
          items={items}
          disabled={disabled}
          onChangeCheckbox={onChangeCheckbox}
        />
      </div>
      {isCompanyAdmin && (
        <>
          <p className="access-page__additional-users-description">
            Need to add or remove additional users?
          </p>
          <PrimaryButton
            className="access-page-primary-button"
            onClick={() => history.push(SettingsRoutes.users.getLink())}
          >
            Go to Account Settings
          </PrimaryButton>
        </>
      )}
    </SettingSection>
  );
};

export default enhance(AccessUsers);
